import { useCallback } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { Typeahead } from "@/components/Typeahead";
import {
  searchSubmissionCodes,
  type CodeOption,
} from "@/lib/brokerSubmissions";

// A single class-code typeahead SCOPED to the submission (PMS-S156/BRK-S065):
// searchSubmissionCodes returns only the codes the submission's product is in
// appetite for in the insured's state. Extracted from the WC rating form so each
// exposure row owns one (BRK-S047, DRY). When the page is empty for a SCOPING
// reason (no state, or no in-appetite codes for the state) it explains why
// rather than showing a bare empty dropdown. A failed search is non-fatal.
//
// The debounce, the two-character floor, the cancellation and the selected chip
// now live in the shared `Typeahead` (BRK-S138) — a behavior-identical
// extraction, made when the NAICS picker became the second field wanting all of
// it. What stays here is the only thing that is about CLASS CODES: which
// endpoint to call, how a code reads, and what an empty page MEANS, which for a
// scoped search is never "no results".
export function ClassCodeField({
  value,
  onChange,
  submissionId,
}: {
  value: CodeOption | null;
  onChange: (code: CodeOption | null) => void;
  /** The submission the search is scoped to (PMS-S156). */
  submissionId: string;
}) {
  const { authedRequest } = useAuth();

  // Stable across renders — `Typeahead` uses it as an effect dependency, so a
  // fresh closure per render would restart the debounce on every render.
  const search = useCallback(
    async (query: string) => {
      const page = await searchSubmissionCodes(
        authedRequest,
        submissionId,
        query,
        10,
      );
      return {
        items: page.items,
        // Empty-with-reason (PMS-S156): a scoping emptiness gets an
        // explanation, never a bare "no results" — that would be a claim about
        // appetite nobody made. A plain search miss (reason null) falls through
        // to the shared default.
        emptyMessage:
          page.reason === "no_codes_for_state"
            ? `No eligible class codes for ${page.state ?? "this state"} on this product — contact us to confirm appetite.`
            : page.reason === "no_state"
              ? "Add the insured’s state on the profile step to see eligible class codes."
              : undefined,
      };
    },
    [authedRequest, submissionId],
  );

  return (
    <Typeahead
      searchLabel="Search class codes"
      placeholder="Search by code or description"
      search={search}
      selected={value}
      onSelect={onChange}
      keyOf={(c) => c.id}
      renderSelected={(c) => (
        <>
          <span className="font-mono font-medium">{c.code}</span> —{" "}
          {c.friendlyName ?? c.description ?? c.typeName}
        </>
      )}
      renderOption={(c) => (
        <>
          <span className="font-mono font-medium text-brk-text">
            {c.code}{" "}
            <span className="font-sans text-brk-dim">({c.typeName})</span>
          </span>
          <span className="text-xs text-brk-dim">
            {c.friendlyName ?? c.description}
          </span>
        </>
      )}
    />
  );
}
